/**
 * E-Signature Panel Component
 * Comprehensive signature capture with backend integration
 * Supports typed, voice, and handwritten signatures with server-side storage
 */
import React, { useState, useRef, useEffect } from 'react';
import { Mic, Square, Play, Trash2, Pen, Type, RotateCcw, Shield, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { useESignature } from '../hooks/useESignature';
import { toast } from 'sonner';

const ESignaturePanel = ({ 
  label = "Signature", 
  signerName = '',
  signerTitle = '',
  documentType = '',
  documentId = '',
  patientId = '',
  existingSignatureId = null,
  onSignatureComplete,
  onSignatureChange,
  className = '',
  readOnly = false
}) => {
  const { createSignature, verifySignature, loading } = useESignature();
  
  const [activeTab, setActiveTab] = useState('draw'); // 'type', 'voice', 'draw'
  const [typedSignature, setTypedSignature] = useState('');
  const [signatureStatus, setSignatureStatus] = useState(null); // null, 'pending', 'signed', 'verified'
  const [signatureRecord, setSignatureRecord] = useState(null);
  
  // Voice recording states
  const [isRecording, setIsRecording] = useState(false);
  const [audioURL, setAudioURL] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  
  // Handwritten signature states
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawing, setHasDrawing] = useState(false);

  // Verify existing signature on mount
  useEffect(() => {
    const verifyExisting = async () => {
      if (!existingSignatureId) return;
      
      const result = await verifySignature(existingSignatureId);
      if (result.success && result.verification.isValid) {
        setSignatureStatus('verified');
        setSignatureRecord(result.verification);
      }
    };
    
    if (existingSignatureId) {
      verifyExisting();
    }
  }, [existingSignatureId, verifySignature]);

  // ============ CANVAS SETUP ============
  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1a365d';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  useEffect(() => {
    if (activeTab === 'draw') {
      initCanvas();
    }
  }, [activeTab]);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    if (readOnly) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasDrawing(true);
    setSignatureStatus('pending');
  };

  const draw = (e) => {
    if (!isDrawing || readOnly) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      notifyChange();
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setHasDrawing(false);
      setSignatureStatus(null);
      notifyChange();
    }
  };

  // ============ VOICE RECORDING ============
  const startRecording = async () => {
    if (readOnly) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setAudioBlob(blob);
        setAudioURL(url);
        setSignatureStatus('pending');
        stream.getTracks().forEach(track => track.stop());
        notifyChange();
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('Unable to access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const playAudio = () => {
    if (audioRef.current) {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const deleteRecording = () => {
    if (audioURL) {
      URL.revokeObjectURL(audioURL);
    }
    setAudioURL(null);
    setAudioBlob(null);
    setRecordingTime(0);
    setSignatureStatus(null);
    notifyChange();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // ============ TYPED SIGNATURE ============
  const handleTypedChange = (e) => {
    const newTyped = e.target.value;
    setTypedSignature(newTyped);
    setSignatureStatus(newTyped ? 'pending' : null);
    notifyChange();
  };

  // ============ NOTIFICATION ============
  const notifyChange = () => {
    if (onSignatureChange) {
      onSignatureChange({
        typed: typedSignature,
        hasDrawing,
        hasVoice: !!audioURL,
        status: signatureStatus
      });
    }
  };

  // ============ SAVE SIGNATURE TO BACKEND ============
  const handleSaveSignature = async () => {
    let signatureImage = '';
    
    // Get signature data based on active method
    if (activeTab === 'draw' && hasDrawing) {
      const canvas = canvasRef.current;
      signatureImage = canvas.toDataURL('image/png');
    } else if (activeTab === 'type' && typedSignature) {
      // Create a canvas with the typed signature
      const canvas = document.createElement('canvas');
      canvas.width = 400;
      canvas.height = 100;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = 'italic 32px "Brush Script MT", cursive';
      ctx.fillStyle = '#1a365d';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(typedSignature, canvas.width / 2, canvas.height / 2);
      signatureImage = canvas.toDataURL('image/png');
    } else if (activeTab === 'voice' && audioBlob) {
      // For voice signatures, we'll convert the audio to base64
      const reader = new FileReader();
      signatureImage = await new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(audioBlob);
      });
    }

    if (!signatureImage) {
      toast.error('Please provide a signature before saving');
      return;
    }

    const result = await createSignature({
      signatureImage,
      signerName: signerName || typedSignature,
      signerTitle,
      documentType,
      documentId,
      patientId
    });

    if (result.success) {
      setSignatureStatus('signed');
      setSignatureRecord(result.signature);
      toast.success('Signature saved successfully');
      
      if (onSignatureComplete) {
        onSignatureComplete(result.signature);
      }
    } else {
      toast.error(result.error || 'Failed to save signature');
    }
  };

  // ============ RENDER STATUS BADGE ============
  const renderStatusBadge = () => {
    if (signatureStatus === 'verified') {
      return (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm">
          <Shield className="w-4 h-4" />
          <span>Verified</span>
        </div>
      );
    }
    if (signatureStatus === 'signed') {
      return (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm">
          <CheckCircle className="w-4 h-4" />
          <span>Signed</span>
        </div>
      );
    }
    if (signatureStatus === 'pending') {
      return (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-100 text-yellow-700 rounded-full text-sm">
          <AlertCircle className="w-4 h-4" />
          <span>Pending</span>
        </div>
      );
    }
    return null;
  };

  const hasSignatureData = hasDrawing || typedSignature || audioURL;

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center justify-between">
        <Label className="font-semibold text-sm">{label}</Label>
        {renderStatusBadge()}
      </div>
      
      {/* Tab Buttons */}
      {!readOnly && (
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          <button
            type="button"
            onClick={() => setActiveTab('draw')}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'draw' 
                ? 'bg-white text-[#f39c12] shadow-sm' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
            data-testid="signature-draw-tab"
          >
            <Pen className="w-4 h-4" /> Draw
            {hasDrawing && <span className="w-2 h-2 bg-green-500 rounded-full"></span>}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('type')}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'type' 
                ? 'bg-white text-[#f39c12] shadow-sm' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
            data-testid="signature-type-tab"
          >
            <Type className="w-4 h-4" /> Type
            {typedSignature && <span className="w-2 h-2 bg-green-500 rounded-full"></span>}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('voice')}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'voice' 
                ? 'bg-white text-[#f39c12] shadow-sm' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
            data-testid="signature-voice-tab"
          >
            <Mic className="w-4 h-4" /> Voice
            {audioURL && <span className="w-2 h-2 bg-green-500 rounded-full"></span>}
          </button>
        </div>
      )}

      {/* Draw Signature */}
      {activeTab === 'draw' && (
        <div className="space-y-2">
          <div className="relative border-2 border-dashed border-gray-300 rounded-lg bg-white overflow-hidden">
            <canvas
              ref={canvasRef}
              width={500}
              height={150}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              className="w-full cursor-crosshair touch-none"
              style={{ height: '150px' }}
              data-testid="signature-canvas"
            />
            {!hasDrawing && !readOnly && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <p className="text-gray-400 text-sm" data-no-pdf>Sign here with mouse or touch</p>
              </div>
            )}
            {/* Signature Line */}
            <div className="absolute bottom-4 left-4 right-4 border-b border-gray-400"></div>
          </div>
          {!readOnly && (
            <div className="flex justify-end">
              <Button 
                type="button"
                onClick={clearCanvas}
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
                data-testid="clear-signature-btn"
              >
                <RotateCcw className="w-3 h-3" /> Clear
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Type Signature */}
      {activeTab === 'type' && (
        <div className="space-y-2">
          <input
            type="text"
            value={typedSignature}
            onChange={handleTypedChange}
            placeholder="Type your full legal name"
            disabled={readOnly}
            className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#f39c12] focus:border-transparent bg-white"
            style={{ fontFamily: '"Brush Script MT", cursive', fontSize: '24px', color: '#1a365d' }}
            data-testid="signature-typed-input"
          />
          {typedSignature && (
            <div className="p-4 bg-gray-50 rounded-lg border">
              <p className="text-xs text-gray-500 mb-2">Signature Preview:</p>
              <div className="relative border-b border-gray-400 pb-2">
                <p style={{ fontFamily: '"Brush Script MT", cursive', fontSize: '28px', color: '#1a365d' }}>
                  {typedSignature}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Voice Recording */}
      {activeTab === 'voice' && (
        <div className="space-y-3 p-4 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
          <p className="text-xs text-gray-600 text-center">Record your verbal consent as signature verification</p>
          
          {!audioURL ? (
            <div className="flex flex-col items-center gap-3">
              {isRecording ? (
                <>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>
                    <span className="text-red-600 font-medium">Recording... {formatTime(recordingTime)}</span>
                  </div>
                  <Button 
                    type="button"
                    onClick={stopRecording}
                    variant="destructive"
                    className="flex items-center gap-2"
                    data-testid="stop-recording-btn"
                  >
                    <Square className="w-4 h-4" /> Stop Recording
                  </Button>
                </>
              ) : (
                <Button 
                  type="button"
                  onClick={startRecording}
                  disabled={readOnly}
                  className="flex items-center gap-2 bg-[#f39c12] hover:bg-[#e67e22]"
                  data-testid="start-recording-btn"
                >
                  <Mic className="w-4 h-4" /> Start Recording
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-green-700 font-medium">Voice signature recorded</span>
              </div>
              
              <audio 
                ref={audioRef} 
                src={audioURL} 
                onEnded={() => setIsPlaying(false)}
                className="hidden"
              />
              
              <div className="flex justify-center gap-2">
                {!isPlaying ? (
                  <Button 
                    type="button"
                    onClick={playAudio}
                    variant="outline"
                    className="flex items-center gap-2"
                    data-testid="play-audio-btn"
                  >
                    <Play className="w-4 h-4" /> Play
                  </Button>
                ) : (
                  <Button 
                    type="button"
                    onClick={stopAudio}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Square className="w-4 h-4" /> Stop
                  </Button>
                )}
                {!readOnly && (
                  <Button 
                    type="button"
                    onClick={deleteRecording}
                    variant="outline"
                    className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid="delete-recording-btn"
                  >
                    <Trash2 className="w-4 h-4" /> Delete
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Save Button & Status */}
      {!readOnly && documentId && hasSignatureData && signatureStatus === 'pending' && (
        <div className="pt-2">
          <Button
            type="button"
            onClick={handleSaveSignature}
            disabled={loading}
            className="w-full bg-green-600 hover:bg-green-700 text-white"
            data-testid="save-signature-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving Signature...
              </>
            ) : (
              <>
                <Shield className="w-4 h-4 mr-2" /> Save & Verify Signature
              </>
            )}
          </Button>
        </div>
      )}

      {/* Signature Record Info */}
      {signatureRecord && (signatureStatus === 'signed' || signatureStatus === 'verified') && (
        <div className="p-3 bg-gray-50 rounded-lg border text-xs text-gray-600">
          <div className="flex items-center gap-2 mb-1">
            <Shield className="w-3 h-3 text-green-600" />
            <span className="font-medium">Electronic Signature Record</span>
          </div>
          <p>Signed: {new Date(signatureRecord.signedAt || signatureRecord.signedAt).toLocaleString()}</p>
          <p className="font-mono text-[10px] text-gray-400 mt-1 break-all">
            ID: {signatureRecord.id || signatureRecord.signatureId}
          </p>
        </div>
      )}
    </div>
  );
};

export default ESignaturePanel;
